import type { OarTaskService } from '../../services/oar-task-service';
import type { ClaimAnalysisResultModel } from '../../models/claim-analysis-result-model';
import type { ClaimAnalysisResponse } from '../../types/claim-analysis';
import type { AnalyzeClaimsCommand } from '../claim-analysis-command';

/**
 * 分析权利要求命令参数
 */
export interface AnalyzeClaimsCommandParams {
    taskId: string;
    conclusiveOpinionId: string;
}

/**
 * 分析权利要求命令实现
 *
 * 执行流程：
 * 1. 调用 OarTaskService.analyzeClaims 获取分析结果
 * 2. 将结果更新到 ClaimAnalysisResultModel
 * 3. 返回分析结果
 */
export class AnalyzeClaimsCommandImpl implements AnalyzeClaimsCommand {
    private _service: OarTaskService;
    private _model: ClaimAnalysisResultModel;
    private _previousData?: ClaimAnalysisResponse;
    private _params: AnalyzeClaimsCommandParams;

    constructor(
        service: OarTaskService,
        model: ClaimAnalysisResultModel,
        params: AnalyzeClaimsCommandParams,
    ) {
        this._service = service;
        this._model = model;
        this._params = params;
    }

    /**
     * 检查命令是否可以执行
     *
     * @returns 是否可以执行
     */
    canExecute(): boolean {
        return !!this._params.taskId && !!this._params.conclusiveOpinionId;
    }

    /**
     * 执行分析权利要求命令
     *
     * @returns 权利要求分析结果
     * @throws 当参数无效时抛出错误
     */
    async execute(): Promise<ClaimAnalysisResponse> {
        if (!this.canExecute()) {
            throw new Error('Invalid command parameters: taskId and conclusiveOpinionId are required');
        }

        try {
            // 1. 保存当前数据用于撤销
            this._previousData = this._model.modelData;

            // 2. 调用服务获取分析结果
            const result = await this._service.analyzeClaims(
                this._params.taskId,
                this._params.conclusiveOpinionId,
            );

            // 3. 更新 Model
            this._model.update(() => result);

            return result;
        } catch (error) {
            // 如果执行失败，恢复之前的状态
            if (this._previousData) {
                this._model.update(() => this._previousData);
            }
            throw error;
        }
    }

    /**
     * 撤销命令，恢复到执行前的状态
     */
    async undo(): Promise<void> {
        if (this._previousData) {
            this._model.update(() => this._previousData);
            this._previousData = undefined;
        }
    }
}
